<script setup lang="ts">
import VText from '~/components/ui/VText.vue'

import { useSeoListUrlDataToEntity } from '~/composables/useSeoListUrlDataToEntity'
import { useGetTranslatedTagEntityName } from '~/composables/useTagHelpers'

import type { SeoListsAsyncData } from '~/types/influencerListTagSeo'
import type { I18nTextKey } from '~/types/seoList'
import type TagTypeKey from '~/types/tagTypeKey'

type Props = {
  urlData: SeoListsAsyncData['urlData']
  influencerCount: number
}
const props = defineProps<Props>()

const { t, locale } = useI18n()
const getTranslatedTagEntityName = useGetTranslatedTagEntityName()
const { entities } = useSeoListUrlDataToEntity(props.urlData)

const text = computed<string>(() => {
  if (props.urlData.length > 1) {
    // We don't need to check priority here since we read from the url directly!
    const i18nKey = props.urlData
      .map((data) => data.tagTypeName)
      .join('_') as I18nTextKey

    return t(`components.seo.subtitle.dualComposition.${i18nKey}`, {
      ...entities.value.reduce(
        (accumulator, data, index) => {
          const type = props.urlData[index].tagTypeName

          accumulator[type] = getTranslatedTagEntityName(data, type, 2)
          if (type !== 'country' || locale.value === 'fr')
            accumulator[type] = accumulator[type].toLowerCase()

          return accumulator
        },
        {} as Record<TagTypeKey, string>,
      ),
      count: props.influencerCount,
    }) as string
  } else if (props.urlData[0].tagTypeName !== 'influencer_kind') {
    const type = props.urlData[0].tagTypeName
    let tagName = getTranslatedTagEntityName(
      entities.value[0],
      type === 'country' ? 'nationalities' : type,
      2,
    )

    if (props.urlData[0].tagTypeName !== 'country' || locale.value === 'fr')
      tagName = tagName.toLowerCase()

    return t(`components.seo.subtitle.simple`, {
      tagName,
      count: props.influencerCount,
    })
  } else {
    return t(`components.seo.subtitle.simple_influencer_kind_edge_case`, {
      influencer_kind: getTranslatedTagEntityName(
        entities.value[0],
        props.urlData[0].tagTypeName,
        2,
      ).toLowerCase(),
      count: props.influencerCount,
    })
  }
})
</script>

<template>
  <VText v-once cfg="sans/16/regular">{{ text }}</VText>
</template>
